import { useEffect, useState, type FormEvent } from 'react'
import type { Session } from '@supabase/supabase-js'
import {
  deleteNewsItem,
  getInquiryItems,
  getNewsItems,
  isCloudStorageEnabled,
  getStorageModeLabel,
  saveNewsItem,
  type InquiryItem,
  type NewsItem,
} from '../../lib/siteContent'
import { usePageSeo } from '../../lib/seo'
import { getSupabaseSession, signInAdmin, signOutAdmin, subscribeAuthStateChange } from '../../lib/supabase'

type AdminTab = 'setup' | 'news' | 'inquiries'

interface NewsFormState {
  id?: string
  title: string
  category: string
  summary: string
  body: string
  publishedAt: string
  isPublished: boolean
}

const navigationItems = [
  { href: '/', label: 'サイトトップ' },
  { href: '/news', label: 'お知らせ一覧' },
  { href: '/contact', label: 'お問い合わせ' },
]

function getToday() {
  const now = new Date()
  const offset = now.getTimezoneOffset()
  return new Date(now.getTime() - offset * 60_000).toISOString().slice(0, 10)
}

function createEmptyNewsForm(): NewsFormState {
  return {
    title: '',
    category: 'お知らせ',
    summary: '',
    body: '',
    publishedAt: getToday(),
    isPublished: true,
  }
}

export default function Admin() {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [activeTab, setActiveTab] = useState<AdminTab>('setup')
  const [newsItems, setNewsItems] = useState<NewsItem[]>([])
  const [inquiryItems, setInquiryItems] = useState<InquiryItem[]>([])
  const [newsForm, setNewsForm] = useState<NewsFormState>(createEmptyNewsForm())
  const [session, setSession] = useState<Session | null>(null)
  const [isLoadingSession, setIsLoadingSession] = useState(true)
  const [authError, setAuthError] = useState('')
  const [authForm, setAuthForm] = useState({ email: '', password: '' })
  const [isSigningIn, setIsSigningIn] = useState(false)

  usePageSeo({
    title: 'ホームページ管理 | 合同会社サクラネクサスメディア',
    description: '合同会社サクラネクサスメディアのホームページ管理画面です。',
    path: '/admin',
    noindex: true,
  })

  const isCloudMode = isCloudStorageEnabled()
  const canManageCloudData = !isCloudMode || Boolean(session)

  const refresh = async () => {
    const [nextNewsItems, nextInquiryItems] = await Promise.all([getNewsItems(), getInquiryItems()])
    setNewsItems(nextNewsItems)
    setInquiryItems(nextInquiryItems)
  }

  useEffect(() => {
    if (!isCloudMode) {
      setIsLoadingSession(false)
      void refresh()
      return
    }

    void getSupabaseSession().then((nextSession) => {
      setSession(nextSession)
      setIsLoadingSession(false)
      if (nextSession) {
        void refresh()
      }
    })

    const unsubscribe = subscribeAuthStateChange((nextSession) => {
      setSession(nextSession)
      setIsLoadingSession(false)
      if (nextSession) {
        void refresh()
      } else {
        setNewsItems([])
        setInquiryItems([])
      }
    })

    return unsubscribe
  }, [])

  const handleNewsSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    await saveNewsItem(newsForm)
    setNewsForm(createEmptyNewsForm())
    await refresh()
  }

  const handleEditNews = (item: NewsItem) => {
    setActiveTab('news')
    setNewsForm({
      id: item.id,
      title: item.title,
      category: item.category,
      summary: item.summary,
      body: item.body,
      publishedAt: item.publishedAt,
      isPublished: item.isPublished,
    })
  }

  const handleDeleteNews = async (id: string) => {
    await deleteNewsItem(id)
    if (newsForm.id === id) {
      setNewsForm(createEmptyNewsForm())
    }
    await refresh()
  }

  const handleSignIn = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setIsSigningIn(true)
    setAuthError('')

    try {
      await signInAdmin(authForm.email, authForm.password)
      await refresh()
    } catch (error) {
      setAuthError(error instanceof Error ? error.message : 'ログインに失敗しました。')
    } finally {
      setIsSigningIn(false)
    }
  }

  const handleSignOut = async () => {
    await signOutAdmin()
  }

  const downloadFile = (filename: string, content: string, type: string) => {
    const blob = new Blob([content], { type })
    const url = window.URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = filename
    anchor.click()
    window.URL.revokeObjectURL(url)
  }

  const handleExportNews = () => {
    downloadFile(
      `sakuranexus-news-${getToday()}.json`,
      JSON.stringify(newsItems, null, 2),
      'application/json;charset=utf-8',
    )
  }

  const handleExportInquiries = () => {
    const lines = [
      ['日時', '名前', '会社名', 'メールアドレス', 'お問い合わせ種別', '内容', '送信状態'].join(','),
      ...inquiryItems.map((item) =>
        [
          `"${new Date(item.createdAt).toLocaleString('ja-JP').replaceAll('"', '""')}"`,
          `"${item.name.replaceAll('"', '""')}"`,
          `"${item.company.replaceAll('"', '""')}"`,
          `"${item.email.replaceAll('"', '""')}"`,
          `"${item.subject.replaceAll('"', '""')}"`,
          `"${item.message.replaceAll('"', '""').replaceAll('\n', ' ')}"`,
          `"${item.syncStatus}"`,
        ].join(','),
      ),
    ]

    downloadFile(
      `sakuranexus-inquiries-${getToday()}.csv`,
      lines.join('\n'),
      'text/csv;charset=utf-8',
    )
  }

  return (
    <div className="bg-slate-50 text-slate-900">
      <header className="sticky top-0 z-50 border-b border-slate-200 bg-white/92 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
          <a href="/" className="flex flex-col">
            <div>
              <p className="text-xs uppercase tracking-[0.35em] text-slate-400">Site Manager</p>
              <p className="text-sm font-semibold text-slate-900">ホームページ管理</p>
            </div>
          </a>

          <nav className="hidden items-center gap-7 lg:flex">
            {navigationItems.map((item) => (
              <a key={item.href} href={item.href} className="text-sm font-medium text-slate-600 transition hover:text-slate-900">
                {item.label}
              </a>
            ))}
          </nav>

          <div className="hidden items-center gap-3 lg:flex">
            <span className="rounded-full border border-slate-200 bg-slate-50 px-4 py-2 text-xs font-semibold text-slate-500">
              保存方式: {getStorageModeLabel()}
            </span>
          </div>

          <button
            type="button"
            onClick={() => setIsMenuOpen((open) => !open)}
            className="flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 text-slate-800 lg:hidden"
            aria-label="メニューを開く"
          >
            <i className={`${isMenuOpen ? 'ri-close-line' : 'ri-menu-3-line'} text-xl`}></i>
          </button>
        </div>

        {isMenuOpen && (
          <div className="border-t border-slate-200 bg-white lg:hidden">
            <nav className="mx-auto flex max-w-7xl flex-col px-4 py-4 sm:px-6">
              {navigationItems.map((item) => (
                <a
                  key={item.href}
                  href={item.href}
                  onClick={() => setIsMenuOpen(false)}
                  className="border-b border-slate-200 py-3 text-sm font-medium text-slate-700 last:border-b-0"
                >
                  {item.label}
                </a>
              ))}
            </nav>
          </div>
        )}
      </header>

      <main className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <section className="rounded-[2rem] border border-slate-200 bg-white p-8 shadow-[0_20px_50px_rgba(15,23,42,0.05)]">
          <p className="section-eyebrow">Manager</p>
          <h1 className="mt-4 text-4xl font-semibold text-slate-950">ホームページ管理</h1>
          <p className="mt-5 max-w-4xl text-sm leading-8 text-slate-600">
            お知らせの登録と、お問い合わせ内容の確認ができる管理ページです。
            {isCloudMode
              ? ' Supabase に接続されている場合は、管理者ログイン後に共有データを扱えます。'
              : ' 現在はブラウザ保存で動いているため、この端末・このブラウザで保存された内容を表示します。'}
          </p>
        </section>

        {isCloudMode && (
          <section className="mt-8 rounded-[2rem] border border-slate-200 bg-white p-8 shadow-[0_20px_50px_rgba(15,23,42,0.05)]">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <h2 className="text-2xl font-semibold text-slate-950">Supabase 管理ログイン</h2>
                <p className="mt-3 max-w-3xl text-sm leading-8 text-slate-600">
                  Supabase のメールアドレスとパスワードでログインすると、お知らせ管理とお問い合わせ一覧を共有データとして扱えます。
                </p>
              </div>
              {session && (
                <button type="button" onClick={() => void handleSignOut()} className="subtle-button">
                  ログアウト
                </button>
              )}
            </div>

            {isLoadingSession ? (
              <p className="mt-6 text-sm text-slate-500">セッションを確認しています。</p>
            ) : session ? (
              <div className="mt-6 rounded-[1.5rem] border border-emerald-200 bg-emerald-50 px-5 py-4 text-sm text-emerald-700">
                {session.user.email} でログイン中です。
              </div>
            ) : (
              <form onSubmit={handleSignIn} className="mt-8 grid gap-5 lg:max-w-3xl lg:grid-cols-[1fr_1fr_auto]">
                <label className="block">
                  <span className="form-label">メールアドレス</span>
                  <input
                    type="email"
                    className="form-input"
                    value={authForm.email}
                    onChange={(event) => setAuthForm((current) => ({ ...current, email: event.target.value }))}
                    required
                  />
                </label>
                <label className="block">
                  <span className="form-label">パスワード</span>
                  <input
                    type="password"
                    className="form-input"
                    value={authForm.password}
                    onChange={(event) => setAuthForm((current) => ({ ...current, password: event.target.value }))}
                    required
                  />
                </label>
                <div className="flex items-end">
                  <button type="submit" disabled={isSigningIn} className="brand-button disabled:opacity-60">
                    {isSigningIn ? 'ログイン中...' : 'ログイン'}
                  </button>
                </div>
              </form>
            )}

            {authError && <p className="mt-4 text-sm text-rose-600">{authError}</p>}
          </section>
        )}

        <section className="mt-8 flex gap-3">
          <button
            type="button"
            onClick={() => setActiveTab('setup')}
            className={`rounded-full px-5 py-3 text-sm font-semibold transition ${
              activeTab === 'setup' ? 'bg-slate-900 text-white' : 'border border-slate-200 bg-white text-slate-600'
            }`}
          >
            セットアップ
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('news')}
            className={`rounded-full px-5 py-3 text-sm font-semibold transition ${
              activeTab === 'news' ? 'bg-slate-900 text-white' : 'border border-slate-200 bg-white text-slate-600'
            }`}
          >
            お知らせ管理
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('inquiries')}
            className={`rounded-full px-5 py-3 text-sm font-semibold transition ${
              activeTab === 'inquiries' ? 'bg-slate-900 text-white' : 'border border-slate-200 bg-white text-slate-600'
            }`}
          >
            お問い合わせ一覧
          </button>
        </section>

        {activeTab === 'setup' && (
          <section className="mt-8 grid gap-8 lg:grid-cols-[0.95fr_1.05fr]">
            <div className="rounded-[2rem] border border-slate-200 bg-white p-8 shadow-[0_20px_50px_rgba(15,23,42,0.05)]">
              <h2 className="text-2xl font-semibold text-slate-950">現在の状態</h2>
              <div className="mt-8 space-y-4">
                <div className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-5">
                  <p className="text-xs uppercase tracking-[0.24em] text-slate-400">Storage</p>
                  <p className="mt-2 text-lg font-semibold text-slate-950">{getStorageModeLabel()}</p>
                  <p className="mt-2 text-sm leading-7 text-slate-600">
                    {isCloudMode
                      ? 'Supabase が設定されています。管理者ログイン後は共有データとして扱えます。'
                      : 'まだ Supabase 未設定のため、この端末のブラウザ保存で動作しています。'}
                  </p>
                </div>
                <div className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-5">
                  <p className="text-xs uppercase tracking-[0.24em] text-slate-400">Routes</p>
                  <div className="mt-3 space-y-2 text-sm leading-7 text-slate-600">
                    <p>`/admin` : 管理画面</p>
                    <p>`/news` : お知らせ一覧</p>
                    <p>`/news/:slug` : お知らせ詳細</p>
                    <p>`/contact` : お問い合わせフォーム</p>
                  </div>
                </div>
                <div className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-5">
                  <p className="text-xs uppercase tracking-[0.24em] text-slate-400">Data</p>
                  <div className="mt-3 space-y-2 text-sm leading-7 text-slate-600">
                    <p>お知らせ: {newsItems.length} 件</p>
                    <p>お問い合わせ: {inquiryItems.length} 件</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-[2rem] border border-slate-200 bg-white p-8 shadow-[0_20px_50px_rgba(15,23,42,0.05)]">
              <h2 className="text-2xl font-semibold text-slate-950">次の手順</h2>
              <div className="mt-8 space-y-5 text-sm leading-8 text-slate-600">
                <div className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-5">
                  <p className="font-semibold text-slate-950">1. `.env` を作成</p>
                  <p className="mt-2">`VITE_SUPABASE_URL` と `VITE_SUPABASE_ANON_KEY` を設定します。</p>
                </div>
                <div className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-5">
                  <p className="font-semibold text-slate-950">2. SQL を実行</p>
                  <p className="mt-2">`supabase/schema.sql` を Supabase の SQL Editor で実行します。</p>
                </div>
                <div className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-5">
                  <p className="font-semibold text-slate-950">3. 管理者ユーザー作成</p>
                  <p className="mt-2">Supabase Auth で管理用ユーザーを作成し、この画面からログインします。</p>
                </div>
                <div className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-5">
                  <p className="font-semibold text-slate-950">4. 既存データの引き継ぎ</p>
                  <p className="mt-2">必要であれば下の書き出し機能でローカル保存データを退避してから移行できます。</p>
                </div>
              </div>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <button type="button" onClick={handleExportNews} className="subtle-button">
                  お知らせをJSON書き出し
                </button>
                <button type="button" onClick={handleExportInquiries} className="subtle-button">
                  お問い合わせをCSV書き出し
                </button>
              </div>
            </div>
          </section>
        )}

        {!canManageCloudData && isCloudMode && !isLoadingSession && (
          <section className="mt-8 rounded-[2rem] border border-dashed border-slate-200 bg-white px-6 py-12 text-center text-sm leading-8 text-slate-500 shadow-[0_20px_50px_rgba(15,23,42,0.03)]">
            管理データを表示するには、上のログインフォームから管理者ログインしてください。
          </section>
        )}

        {activeTab === 'news' && canManageCloudData && (
          <section className="mt-8 grid gap-8 lg:grid-cols-[0.95fr_1.05fr]">
            <div className="rounded-[2rem] border border-slate-200 bg-white p-8 shadow-[0_20px_50px_rgba(15,23,42,0.05)]">
              <div className="flex items-center justify-between gap-4">
                <h2 className="text-2xl font-semibold text-slate-950">{newsForm.id ? 'お知らせを編集' : 'お知らせを追加'}</h2>
                {newsForm.id && (
                  <button
                    type="button"
                    onClick={() => setNewsForm(createEmptyNewsForm())}
                    className="text-sm font-semibold text-slate-500 transition hover:text-slate-900"
                  >
                    新規作成に戻す
                  </button>
                )}
              </div>

              <form onSubmit={handleNewsSubmit} className="mt-8 space-y-5">
                <label className="block">
                  <span className="form-label">タイトル</span>
                  <input
                    type="text"
                    className="form-input"
                    value={newsForm.title}
                    onChange={(event) => setNewsForm((current) => ({ ...current, title: event.target.value }))}
                    required
                  />
                </label>

                <div className="grid gap-5 sm:grid-cols-2">
                  <label className="block">
                    <span className="form-label">カテゴリ</span>
                    <input
                      type="text"
                      className="form-input"
                      value={newsForm.category}
                      onChange={(event) => setNewsForm((current) => ({ ...current, category: event.target.value }))}
                      required
                    />
                  </label>
                  <label className="block">
                    <span className="form-label">公開日</span>
                    <input
                      type="date"
                      className="form-input"
                      value={newsForm.publishedAt}
                      onChange={(event) => setNewsForm((current) => ({ ...current, publishedAt: event.target.value }))}
                      required
                    />
                  </label>
                </div>

                <label className="block">
                  <span className="form-label">一覧用の要約</span>
                  <textarea
                    className="form-input min-h-[110px] resize-y"
                    value={newsForm.summary}
                    onChange={(event) => setNewsForm((current) => ({ ...current, summary: event.target.value }))}
                    required
                  ></textarea>
                </label>

                <label className="block">
                  <span className="form-label">詳細本文</span>
                  <textarea
                    className="form-input min-h-[220px] resize-y"
                    value={newsForm.body}
                    onChange={(event) => setNewsForm((current) => ({ ...current, body: event.target.value }))}
                    required
                  ></textarea>
                </label>

                <label className="flex items-center gap-3 text-sm text-slate-600">
                  <input
                    type="checkbox"
                    checked={newsForm.isPublished}
                    onChange={(event) => setNewsForm((current) => ({ ...current, isPublished: event.target.checked }))}
                  />
                  公開する
                </label>

                <button type="submit" className="brand-button">
                  {newsForm.id ? '更新する' : '登録する'}
                </button>
              </form>
            </div>

            <div className="rounded-[2rem] border border-slate-200 bg-white p-8 shadow-[0_20px_50px_rgba(15,23,42,0.05)]">
              <h2 className="text-2xl font-semibold text-slate-950">登録済みのお知らせ</h2>
              <div className="mt-8 space-y-4">
                {newsItems.map((item) => (
                  <div key={item.id} className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-5">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div>
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                          <span className="text-sm font-semibold text-slate-500">{item.publishedAt.replaceAll('-', '.')}</span>
                          <span className="inline-flex w-fit rounded-full bg-white px-3 py-1 text-xs font-semibold text-slate-600">
                            {item.category}
                          </span>
                          <span className={`inline-flex w-fit rounded-full px-3 py-1 text-xs font-semibold ${item.isPublished ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
                            {item.isPublished ? '公開中' : '非公開'}
                          </span>
                        </div>
                        <h3 className="mt-3 text-lg font-semibold text-slate-950">{item.title}</h3>
                        <p className="mt-2 text-sm leading-7 text-slate-600">{item.summary}</p>
                      </div>
                      <div className="flex gap-3">
                        <a href={`/news/${item.slug}`} className="text-sm font-semibold text-slate-500 transition hover:text-slate-900">
                          詳細
                        </a>
                        <button type="button" onClick={() => handleEditNews(item)} className="text-sm font-semibold text-slate-500 transition hover:text-slate-900">
                          編集
                        </button>
                        <button type="button" onClick={() => void handleDeleteNews(item.id)} className="text-sm font-semibold text-rose-500 transition hover:text-rose-700">
                          削除
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
                {newsItems.length === 0 && (
                  <div className="rounded-[1.5rem] border border-dashed border-slate-200 bg-white px-5 py-8 text-center text-sm text-slate-500">
                    お知らせはまだ登録されていません。
                  </div>
                )}
              </div>
            </div>
          </section>
        )}

        {activeTab === 'inquiries' && canManageCloudData && (
          <section className="mt-8 rounded-[2rem] border border-slate-200 bg-white p-8 shadow-[0_20px_50px_rgba(15,23,42,0.05)]">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <h2 className="text-2xl font-semibold text-slate-950">お問い合わせ一覧</h2>
                <p className="mt-2 text-sm leading-7 text-slate-600">
                  フォームから送信された内容を確認できます。
                </p>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm font-semibold text-slate-400">{inquiryItems.length} 件</span>
                <button type="button" onClick={handleExportInquiries} className="subtle-button">
                  CSV書き出し
                </button>
              </div>
            </div>

            <div className="mt-8 space-y-4">
              {inquiryItems.map((item) => (
                <article key={item.id} className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-6">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                        <span className="text-sm font-semibold text-slate-500">
                          {new Date(item.createdAt).toLocaleString('ja-JP')}
                        </span>
                        <span className={`inline-flex w-fit rounded-full px-3 py-1 text-xs font-semibold ${item.syncStatus === 'submitted' ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}>
                          {item.syncStatus === 'submitted' ? '送信成功' : '送信失敗'}
                        </span>
                      </div>
                      <h3 className="mt-3 text-lg font-semibold text-slate-950">{item.name}</h3>
                      <p className="mt-1 text-sm text-slate-600">{item.company || '会社名未入力'}</p>
                      <p className="mt-1 text-sm text-slate-600">{item.email}</p>
                      <p className="mt-4 text-sm font-semibold text-slate-700">{item.subject || 'お問い合わせ種別未選択'}</p>
                    </div>
                  </div>
                  <div className="mt-4 rounded-[1.2rem] border border-slate-200 bg-white p-4 text-sm leading-7 text-slate-700">
                    {item.message}
                  </div>
                </article>
              ))}

              {inquiryItems.length === 0 && (
                <div className="rounded-[1.5rem] border border-dashed border-slate-200 bg-white px-5 py-10 text-center text-sm text-slate-500">
                  まだお問い合わせはありません。
                </div>
              )}
            </div>
          </section>
        )}
      </main>
    </div>
  )
}
